import { ResourceQueryKeys, UserQueryKeys } from '../queries';
import { createGTMGAEvent } from '@jooxter/utils';
import { FloorQueryKeys } from '@jooxter/floors';
import { generateToast } from '@jooxter/ui';
import {
  useMutateDeleteFavoriteResource,
  useMutateDeleteFavoriteUser,
  useMutatePutFavoriteResource,
  useMutatePutFavoriteUser,
} from '../mutations';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

export const useFavoriteActions = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { mutateAsync: putFavoriteResource } = useMutatePutFavoriteResource();
  const { mutateAsync: deleteFavoriteResource } = useMutateDeleteFavoriteResource();
  const { mutateAsync: putFavoriteUser } = useMutatePutFavoriteUser();
  const { mutateAsync: deleteFavoriteUser } = useMutateDeleteFavoriteUser();

  /**
   * Resolves once the favorite is saved and the queries displaying it are refetched, so an
   * optimistic star (JxtStar) hands over to fresh server data without flickering.
   * Never rejects: errors are reported with a toast and the star falls back to its server value.
   */
  const handleToggleFavorite = useCallback(
    async (favorite: boolean, id: number, tagCategory: string): Promise<void> => {
      const isUser = tagCategory === 'Collaborators' || tagCategory === 'Onboarding';
      createGTMGAEvent(
        tagCategory,
        favorite ? 'Add Favorite' : 'Remove Favorite',
        favorite ? 'Add Favorite' : 'Remove Favorite'
      );

      try {
        if (isUser) {
          await (favorite ? putFavoriteUser(id) : deleteFavoriteUser(id));
        } else {
          await (favorite ? putFavoriteResource(id) : deleteFavoriteResource(id));
        }
      } catch {
        generateToast(t('unknown-error'), true);
        return;
      }

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [ResourceQueryKeys.GetResource, id] }),
        queryClient.invalidateQueries({ queryKey: [ResourceQueryKeys.GetResourcesList] }),
        queryClient.invalidateQueries({ queryKey: [FloorQueryKeys.GetFloorPlanResources] }),
        queryClient.invalidateQueries({ queryKey: [UserQueryKeys.GetUsersSearch] }),
        queryClient.invalidateQueries({ queryKey: [UserQueryKeys.GetFavoritesWorkplaces] }),
        queryClient.invalidateQueries({ queryKey: [UserQueryKeys.GetWorkplaces] }),
      ]);
    },
    [t, queryClient, putFavoriteResource, deleteFavoriteResource, putFavoriteUser, deleteFavoriteUser]
  );

  return { handleToggleFavorite };
};
